import { Help, Lightbulb } from "@mui/icons-material";
import { Box, Card, CardContent, Stack, Theme, useTheme } from "@mui/material";
import { Handle, Position } from "@xyflow/react";
import { useMemo } from "react";
import {
  Character,
  Clue,
  ManagedAnswer,
  StepChallenge,
} from "../../directus/collections";
import { Translated } from "../../directus/directusTypes";
import { getAssetUrl } from "../../directus/utils";
import { useAvgTimeToEnigmaEvent, useAvgTimeToEvent } from "../../hooks";
import { MatchFilter } from "../../libs/statsFilters";
import { ChatItemCard } from "../ChatElementCard.component";
import { ChatItemStatsSubheader } from "../ChatItemStatsSubheader.component";
import {
  formatChallengeContent,
  formatMedia,
} from "../CityEscapeFlowChart/nodes";
import { EnigmaAcceptedAnswersBox } from "../EnigmaAcceptedAnswersBox.compoenent";
import { LangSwitchOption } from "../../types/misc";

export const BASE_NODE_STYLE = {
  padding: 1.5,
  textAlign: "center" as const,
  borderRadius: 2,
  cursor: "pointer",
  display: "flex",
  flexDirection: "column" as const,
  justifyContent: "center",
  alignItems: "center",
};

const NodeHandles = () => {
  const theme = useTheme();
  return (
    <>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: theme.palette.primary.main }}
      />
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: theme.palette.primary.main }}
      />
    </>
  );
};

export const CtaButton = ({ label, backgroundColor, color }: { label: string, backgroundColor?: string, color?: string }) => {
  const theme = useTheme();
  return (
    <div
      style={{
        backgroundColor: backgroundColor || theme.palette.primary.main,
        color: color || theme.palette.primary.contrastText,
        borderRadius: "4px",
        fontWeight: "bold",
        textAlign: "center",
        border: `1px solid ${theme.palette.primary.dark}`,
        cursor: "pointer",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        flex: 1,
      }}
      dangerouslySetInnerHTML={{ __html: label }}
    ></div>
  );
};

function formatStepListItemAvatar(character: Character) {
  if (!character?.avatar) return undefined;

  return {
    url: getAssetUrl(import.meta.env.VITE_DIRECTUS_URL, character.avatar.id),
    alt: character.avatar.title || "Character",
  };
}

type TranslatableItem<T> = {
  translations: Translated<T>;
};

function getActiveTranslation<T>(
  item: TranslatableItem<T> | null | undefined,
  lang: LangSwitchOption
) {
  const translations = item?.translations;
  for (const translation of translations || []) {
    if (translation.languages_code === lang) {
      return translation;
    }
  }

  return translations?.[0];
}

export const EnigmaInitNode = ({
  data,
}: {
  data: {
    challenge: StepChallenge;
    cityEscapeId: string;
    theme: Theme;
    activeLang: LangSwitchOption;
  };
}) => {
  const step = useMemo(() => {
    return formatChallengeContent(data.challenge, {
      lang: data.activeLang,
    });
  }, [data.challenge, data.activeLang]);

  return (
    <div>
      <ChatItemCard
        item={step}
        theme={data.theme}
        activeLang={data.activeLang}
      />
      <Handle position={Position.Bottom} type="source" />
    </div>
  );
};

export const EnigmaHint = ({
  cityEscapeId,
  challengeId,
  hint,
  filters,
  theme,
  index,
}: {
  cityEscapeId: string;
  challengeId: string;
  filters?: MatchFilter;
  hint: Clue;
  theme: Theme;
  index: number;
}) => {
  const stats = useAvgTimeToEnigmaEvent(
    {
      eventType: "challenge_hint",
      value: index.toString(),
    },
    cityEscapeId,
    challengeId,
    filters,
    { traceKey: "EnigmaHint", verbose: false }
  );

  return (
    <ChatItemCard
      key={index}
      item={{
        id: `hint-${index}`,
        heading: {
          avatar: { icon: <Lightbulb /> },
          title: `Ispirazione ${index + 1}`,
          subheader: (
            <ChatItemStatsSubheader
              isLoading={stats.isLoading}
              playRate={stats.data?.playRate || 0}
              // avgDuration={stats.data?.avgTime || 0}
              avgTimeFromStart={stats.data?.avgAbsoluteTime || 0}
            />
          ),
        },
        html: hint.clue,
        // media: formatMedia(hint.media),
      }}
      theme={theme}
    />
  );
};

export const EnigmaSolution = ({
  cityEscapeId,
  challenge,
  filters,
  theme,
  activeLang,
}: {
  cityEscapeId: string;
  challenge: StepChallenge;
  filters?: MatchFilter;
  theme: Theme;
  activeLang: LangSwitchOption;
}) => {
  const activeTranslation = getActiveTranslation(challenge, activeLang);

  const stats = useAvgTimeToEnigmaEvent(
    {
      eventType: "challenge_requested_solution",
    },
    cityEscapeId,
    challenge.id,
    filters,
    { traceKey: "EnigmaSolution", verbose: false }
  );

  return (
    <ChatItemCard
      item={{
        id: `challenge-${challenge.id}-solution`,
        heading: {
          avatar: { icon: <Help /> },
          title: "Soluzione",
          subheader: (
            <ChatItemStatsSubheader
              isLoading={stats.isLoading}
              playRate={stats.data?.playRate || 0}
              // avgDuration={stats.data?.avgTime || 0}
              avgTimeFromStart={stats.data?.avgAbsoluteTime || 0}
            />
          ),
        },
        html: activeTranslation?.solution || "Nessuna soluzione fornita",
        media: formatMedia(activeTranslation?.solution_media || undefined),
      }}
      theme={theme}
      activeLang={activeLang}
    />
  );
};

export const EnigmaHintsNode = ({
  data,
}: {
  data: {
    challenge: StepChallenge;
    cityEscapeId: string;
    theme: Theme;
    filters: MatchFilter;
    activeLang: LangSwitchOption;
  };
}) => {
  const activeTranslation = getActiveTranslation(
    data.challenge,
    data.activeLang
  );

  return (
    <Card style={{ width: 500 }}>
      <Handle position={Position.Top} type="target" />
      <CardContent>
        <Stack spacing={2}>
          {activeTranslation?.clues?.map((hint, index) => (
            <>
              <CtaButton key={index} label={"Ispirazione"} />
              <EnigmaHint
                cityEscapeId={data.cityEscapeId}
                challengeId={data.challenge.id}
                filters={data.filters}
                key={index}
                hint={hint}
                theme={data.theme}
                index={index}
              />
            </>
          ))}
          <CtaButton label={"Mi arrendo"} />
          <EnigmaSolution
            cityEscapeId={data.cityEscapeId}
            challenge={data.challenge}
            filters={data.filters}
            theme={data.theme}
            activeLang={data.activeLang}
          />
        </Stack>
      </CardContent>
      <Handle position={Position.Bottom} type="source" />
    </Card>
  );
};

export const EnigmaSolutionNode = ({
  data,
}: {
  data: {
    challenge: StepChallenge;
    filters?: MatchFilter;
    cityEscapeId: string;
    theme: Theme;
    activeLang: LangSwitchOption;
  };
}) => {
  return (
    <Card style={{ width: 500 }}>
      <Handle position={Position.Top} type="target" />
      <CardContent>
        <Stack spacing={2}>
          <CtaButton label={"Mi arrendo"} />
          <EnigmaSolution
            cityEscapeId={data.cityEscapeId}
            challenge={data.challenge}
            filters={data.filters}
            theme={data.theme}
            activeLang={data.activeLang}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export const EnigmaManagedAnswerBox = ({
  answer,
  boxLabel,
  activeLang,
}: {
  answer: ManagedAnswer;
  boxLabel?: string;
  activeLang: LangSwitchOption;
}) => {
  const activeTranslation = getActiveTranslation(answer, activeLang);
  return (
    <Stack spacing={2} direction={"row"}>
      <Box sx={{ width: 300, maxWidth: 300 }}>
        <EnigmaAcceptedAnswersBox
          label={boxLabel || "Risposte"}
          answers={answer.answers || []}
          matchType={answer.answers_evaluation_rule}
        ></EnigmaAcceptedAnswersBox>
      </Box>
      {activeTranslation?.message && (
        <ChatItemCard
          item={{
            id: `challenge-${answer.id}-response`,
            html: activeTranslation.message,
          }}
        />
      )}
    </Stack>
  );
};

export const EnigmaManagedAnswerNode = ({
  data,
}: {
  data: {
    challenge: StepChallenge;
    cityEscapeId: string;
    answers: ManagedAnswer[];
    theme: Theme;
    activeLang: LangSwitchOption;
  };
}) => {
  return (
    <Card>
      <Handle position={Position.Top} type="target" />
      <CardContent>
        <Stack spacing={2}>
          {data.challenge.managed_answers?.map((answer, index) => (
            <EnigmaManagedAnswerBox
              key={index}
              answer={answer}
              boxLabel={`Quando l'utente risponde`}
              activeLang={data.activeLang}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export const EnigmaSuccessNode = ({
  data,
}: {
  data: {
    challenge: StepChallenge;
    cityEscapeId: string;
    theme: Theme;
    activeLang: LangSwitchOption;
  };
}) => {
  const activeTranslation = getActiveTranslation(
    data.challenge,
    data.activeLang
  );

  return (
    <Card>
      <Handle position={Position.Top} type="target" />
      <CardContent>
        <Stack spacing={2}>
          <Box sx={{ width: 300, maxWidth: 300 }}>
            <EnigmaAcceptedAnswersBox
              label="Risposte accettate"
              answers={data.challenge.possible_correct_answers || []}
              matchType={data.challenge.answers_evaluation_rule}
            ></EnigmaAcceptedAnswersBox>
          </Box>

          {activeTranslation?.correct_answer_response && (
            <ChatItemCard
              item={{
                id: `challenge-${data.challenge.id}-correct-answer-response`,
                html:
                  activeTranslation?.correct_answer_response ||
                  "Nessuna soluzione fornita",
              }}
              theme={data.theme}
              activeLang={data.activeLang}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
